/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { PasswordInput } from '@/components/PasswordInput'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useEffect, useRef } from 'react'
import { useStore } from '@/store/Store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import { z } from 'zod'
import Loading from '@/components/Loading'
import { toast } from 'sonner'
import { useLoginMutation } from '@/hooks/useAuth'
import { useActivatePlanMutation, type PlanKey } from '@/hooks/usePlans'

const Login = () => {
  const formSchema = z.object({
    email: z.email({ message: 'Email invalide' }),
    password: z.string().min(8, {
      message: 'Le mot de passe doit avoir au moins 8 caractères',
    }),
  })

  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const { mutateAsync: login, isPending } = useLoginMutation()
  const { mutateAsync: activatePlan, isPending: isActivating } =
    useActivatePlanMutation()
  const navigate = useNavigate()
  const { search } = useLocation()
  const redirectInUrl = new URLSearchParams(search).get('redirect')
  const redirect = redirectInUrl ? redirectInUrl : '/dashboard'
  const { state } = useStore()
  const { userInfo } = state
  const [params] = useSearchParams()
  const plan = params.get('plan') as PlanKey | null

  const didAutoActivateRef = useRef(false)

  useEffect(() => {
    const autoActivate = async () => {
      if (didAutoActivateRef.current) return
      if (userInfo && plan) {
        try {
          didAutoActivateRef.current = true
          await activatePlan(plan)
          toast.success('Plan activé')
        } catch (e: any) {
          toast.error(
            e?.response?.data?.message ?? 'Activation du plan impossible'
          )
        } finally {
          navigate(redirect, { replace: true })
        }
      } else if (userInfo && !plan) {
        navigate(redirect, { replace: true })
      }
    }
    autoActivate()
  }, [userInfo, plan, activatePlan, navigate, redirect])

  async function onSubmit(values: FormValues) {
    try {
      await login({
        ...values,
        email: values.email.trim().toLowerCase(),
      })

      if (plan) {
        try {
          await activatePlan(plan)
          toast.success('Plan activé')
        } catch (e: any) {
          toast.error(
            e?.response?.data?.message ?? 'Activation du plan impossible'
          )
        }
      }

      toast.success('Connexion réussie')
      navigate(redirect)
    } catch (error) {
      toast.error('Email ou mot de passe incorrect')
    }
  }

  useEffect(() => {
    const ac = new AbortController()
    if (userInfo) {
      navigate(redirect)
    }
    return () => ac.abort()
  }, [redirect, userInfo, navigate])

  return (
    <div className='flex flex-row items-center justify-center h-[100vh] bg-gradient-to-br from-[hsl(0,0%,100%)] via-[hsl(140,13%,91%)] to-[hsl(144,80%,73%)]'>
      <Card className='w-[500px] shadow-2xl rounded-xl'>
        <CardHeader>
          <div className='flex flex-row items-center gap-3'>
            {/* Mode clair */}
            <img
              src='/logo_mogesapp_light.png'
              alt='MOGESAPP'
              className='h-12 w-12 dark:hidden'
              loading='eager'
            />
            {/* Mode sombre */}
            <img
              src='/logo_mogesapp_dark.png'
              alt='MOGESAPP'
              className='h-12 w-12 hidden dark:block'
              loading='eager'
            />
            <h1 className='font-bold text-primary text-4xl'>MOGESAPP</h1>
          </div>

          <hr className='pt-5' />
          <p className='font-semibold text-xl'>Connexion</p>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-8'>
              <FormField
                control={form.control}
                name='email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder='Votre email' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Mot de passe</FormLabel>
                    <FormControl>
                      <PasswordInput placeholder='Mot de passe' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isPending || isActivating ? (
                <Loading />
              ) : (
                <Button disabled={isPending || isActivating} type='submit'>
                  Valider
                </Button>
              )}
            </form>
          </Form>
        </CardContent>
        <CardFooter className='text-muted-foreground flex flex-col justify-center text-center '>
          <div className='mt-2 font-light text-sm'>
            Pas encore de compte?{' '}
            <Link
              to={'/register'}
              className='text-primary hover:text-primary/50'
            >
              Inscrivez-vous
            </Link>
          </div>
          <div className='flex justify-end'>
            <Button
              type='button'
              variant='link'
              className='px-0 h-auto text-muted-foreground hover:text-primary'
              onClick={() => navigate('/forgot-password')}
            >
              Mot de passe oublié ?
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  )
}

export default Login
